import type { ResultOf } from '@graphql-typed-document-node/core'
import DataLoader from 'dataloader'
import { atom, useAtomValue, useSetAtom } from 'jotai'
import { atomWithQuery, queryClientAtom } from 'jotai-tanstack-query'
import { atomFamily } from 'jotai/utils'
import { useEffect } from 'react'
import type { RestaurantId } from '~/lib/graphql/scalars'
import type { Coupon } from './Coupon.frag'

export type DiscountCoupons = ResultOf<typeof Coupon>[]

export function discountCouponsQueryKey(restaurantId: RestaurantId) {
  return ['discountCoupons', restaurantId] as const
}

const discountCouponsLoader = new DataLoader<
  RestaurantId,
  // { data: DiscountCoupons } | { errors: string[] }
  DiscountCoupons
>(
  async (keys) => {
    const url = new URL('/api/v2/discount-coupons', document.URL)
    url.searchParams.append('v', JSON.stringify({ restaurantIds: keys }))
    const result = await fetch(url.pathname + url.search)
    const data = await result.json()
    return data.map((d: { data: DiscountCoupons } | { errors: string[] }) => {
      return 'data' in d ? d.data : []
    })
  },
  {
    batchScheduleFn: (cb) => setTimeout(cb, 50),
    // 同じキーを複数回 fetch しない
    // invalidate したいときは DataLoader と TanStack Query の両方を invalidate しないといけない
    cache: true,
  }
)

const discountCoupons$ = atomFamily((restaurantId: RestaurantId) =>
  atom((get) => {
    const discountCoupons$ = atomWithQuery((_) => {
      return {
        queryKey: discountCouponsQueryKey(restaurantId),
        queryFn: async () => discountCouponsLoader.load(restaurantId),
        staleTime: 24 * 60 * 60 * 1000,
        // 表示したいときは invalidate する前提
        initialData: [],
      }
    })
    return get(discountCoupons$).data
  })
)

export function useDiscountCoupons(
  restaurantId: RestaurantId,
  opts: { invalidate?: boolean } = {}
) {
  const invalidate = useInvalidateDiscountCoupons()
  useEffect(() => {
    if (opts.invalidate) {
      invalidate(restaurantId)
    }
  }, [restaurantId, opts.invalidate])
  return useAtomValue(discountCoupons$(restaurantId))
}

const invalidateDiscountCoupons$ = atom(null, (get, _, restaurantId: RestaurantId) => {
  const client = get(queryClientAtom)
  discountCouponsLoader.clear(restaurantId)
  client.invalidateQueries({ queryKey: discountCouponsQueryKey(restaurantId), exact: true })
})
invalidateDiscountCoupons$.debugLabel = 'invalidateDiscountCoupons$'

export function useInvalidateDiscountCoupons() {
  return useSetAtom(invalidateDiscountCoupons$)
}
